import React, { memo } from 'react';
import { View } from 'react-native';
import { createUseStyle } from '@redlink/assets/styles';
import {
  Text,
  Space,
  Divider,
  IconClose,
  IconPackage,
  IconDelivery,
  IconPassed,
  LoadingContainer,
} from '@redlink/components/atoms';
import { OrderRowItem } from '@redlink/components/muscles';
import { Button } from '@redlink/components/molecules';
import { useLocale } from '@redlink/assets/locale';
import { useValues } from '@redlink/assets/values';

export const OrderItemDetail = memo(
  ({
    item: {
      id,
      contact,
      createDate,
      finalPrice,
      status,
      description,
      items,
    } = {},
    onChangeStatus,
    onChangeItemStatus,
    isBusy,
  }) => {
    const { styles } = useStyle();
    const { dictionary } = useLocale();
    const { staticData } = useValues();

    return (
      <LoadingContainer {...{ isBusy }}>
        <View style={styles.container}>
          <OrderRowItem
            onChange={(value) =>
              onChangeItemStatus(
                id,
                items.map(({ product: { id } }) => id),
                value
              )
            }
            value={items.every(
              ({ status }) => status === staticData.orderItemStatus.done
            )}
            price={finalPrice}
            title={dictionary.total}
            textStyle={styles.totalPriceTitleText}
            priceTextStyle={styles.totalPriceText}
          />
          <Divider margin={8} />
          <ItemList
            {...{ items }}
            onChange={(itemId, value) =>
              onChangeItemStatus(id, [itemId], value)
            }
          />
          <Divider />
          <Space />
          <View style={styles.contactContainer}>
            <Text style={styles.titleText}>{dictionary.contactInfo}</Text>
            <Text style={styles.valueText}>{contact}</Text>
          </View>
          <Space />
          {description && (
            <View>
              <Text style={styles.titleText}>{dictionary.description}</Text>
              <Text style={styles.descriptionText}>{`"${description}"`}</Text>
            </View>
          )}
          <Space />
          <ButtonList
            {...{ status }}
            onChange={(status) => onChangeStatus(id, status)}
          />
        </View>
      </LoadingContainer>
    );
  }
);

const ItemList = memo(({ items, onChange }) => {
  const { staticData } = useValues();
  return items.map(
    (
      {
        product: { id: itemId, title: itemTitle },
        count: itemCount,
        price: itemFinalPrice,
        status,
      },
      index
    ) => {
      return (
        <>
          <OrderRowItem
            onChange={(value) => onChange(itemId, value)}
            price={itemCount * itemFinalPrice}
            count={itemCount}
            title={`${itemTitle}`}
            value={status === staticData.orderItemStatus.done}
          />
          <Space />
        </>
      );
    }
  );
});

const ButtonList = memo(({ status, onChange }) => {
  const { styles, colors } = useStyle();
  const { dictionary } = useLocale();
  const { staticData } = useValues();
  const isCanceled = status === staticData.orderStatus.cancel;
  const isPackaging = status === staticData.orderStatus.packaging;
  const isDelivery = status === staticData.orderStatus.delivery;
  const isDone = status === staticData.orderStatus.done;
  return (
    <>
      <Button
        onPress={() => onChange(staticData.orderStatus.cancel)}
        disabled={isCanceled}
        variant={isCanceled ? 'selected' : 'gray'}
        title={dictionary.orderStatusCancel}
        icon={<IconClose color={!isCanceled ? colors.gray23 : colors.blue1} />}
      />
      <Space size={8} />
      <View style={styles.contactContainer}>
        <Button
          onPress={() => onChange(staticData.orderStatus.packaging)}
          disabled={isPackaging}
          style={styles.twoColumnButton}
          variant={isPackaging ? 'selected' : 'gray'}
          title={dictionary.orderStatusPackaging}
          icon={
            <IconPackage color={!isPackaging ? colors.gray23 : colors.blue1} />
          }
        />
        <Space size={8} />
        <Button
          onPress={() => onChange(staticData.orderStatus.delivery)}
          disabled={isDelivery}
          style={styles.twoColumnButton}
          variant={isDelivery ? 'selected' : 'gray'}
          title={dictionary.orderStatusDelivery}
          icon={
            <IconDelivery color={!isDelivery ? colors.gray23 : colors.blue1} />
          }
        />
      </View>
      <Space size={8} />
      <Button
        onPress={() => onChange(staticData.orderStatus.done)}
        disabled={isDone}
        variant={isDone ? 'selected' : 'gray'}
        title={dictionary.orderStatusDone}
        icon={<IconPassed color={!isDone ? colors.gray23 : colors.blue1} />}
      />
    </>
  );
});

const useStyle = createUseStyle(({ colors, size, shadow }) => ({
  container: {
    padding: 24,
  },
  totalPriceTitleText: {
    fontSize: 16,
    color: colors.blue1,
  },
  totalPriceText: {
    fontWeight: 'bold',
    color: colors.blue1,
    fontSize: 16,
  },
  contactContainer: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  titleText: {
    fontSize: 14,
    color: colors.gray23,
    flex: 1,
  },
  valueText: {
    fontSize: 16,
    color: colors.gray23,
  },
  descriptionText: {
    fontSize: 14,
    color: colors.gray21,
  },
  twoColumnButton: {
    flex: 1,
  },
}));
